import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Container,
  Button,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { getStaticFileUrl } from '../utils/staticUtils';
import useAuthPageTheme from '../hooks/useAuthPageTheme';
import MobileAuthShell from '../components/auth/MobileAuthShell';
import AuthMobileCard from '../components/auth/AuthMobileCard';
import AuthDarkTextField from '../components/auth/AuthDarkTextField';
import AuthBrandingPanel from '../components/auth/AuthBrandingPanel';
import { authFormPanelSx } from '../components/auth/authLightPanelTheme';
import '../components/auth/authPages.css';
import {
  AUTH_COPYRIGHT,
  authCopyrightSx,
  darkPrimaryButtonSx,
  darkAlertSx,
  darkSuccessAlertSx,
} from '../components/auth/loginDarkTheme';

/**
 * Reset Password Page Component
 * Allows users to set a new password after clicking reset link from email
 */
const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const { uidb64, token } = useParams();
  const { isCompact, formPanelThemeClass } = useAuthPageTheme();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Get CSRF token from cookie
  const getCsrfToken = () => {
    const cookies = document.cookie.split(';');
    const csrfCookie = cookies.find(c => c.trim().startsWith('csrftoken='));
    return csrfCookie ? csrfCookie.split('=')[1] : '';
  };

  // Validate passwords
  const validatePasswords = () => {
    const errors = {};
    if (!password.trim()) {
      errors.password = 'Password is required';
    } else if (password.length < 8) {
      errors.password = 'Password must be at least 8 characters long';
    }
    
    if (!confirmPassword.trim()) {
      errors.confirm_password = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      errors.confirm_password = 'Passwords do not match';
    }
    
    return errors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validatePasswords();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setIsSubmitting(true);
    setErrors({});
    setSuccessMessage('');

    try {
      const formData = new FormData();
      formData.append('password', password);
      formData.append('confirm_password', confirmPassword);

      const response = await axios.post(`/password-reset/${uidb64}/${token}/`, formData, {
        headers: {
          'X-CSRFToken': getCsrfToken(),
        },
        withCredentials: true,
      });

      if (response.data.success) {
        setSuccessMessage(response.data.message || 'Your password has been reset successfully.');
        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate('/login/');
        }, 2000);
      } else {
        setErrors(response.data.errors || { non_field_errors: ['An error occurred. Please try again.'] });
      }
    } catch (error) {
      if (error.response && error.response.data) {
        if (error.response.data.errors) {
          setErrors(error.response.data.errors);
        } else {
          setErrors({ non_field_errors: ['Invalid or expired reset link. Please request a new password reset.'] });
        }
      } else {
        setErrors({ non_field_errors: ['An error occurred. Please try again.'] });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const passwordToggle = (visible, toggle) => (
    <InputAdornment position="end">
      <IconButton
        aria-label="toggle password visibility"
        onClick={toggle}
        edge="end"
        sx={{
          color: 'rgba(148, 163, 184, 0.9)',
          '&:hover': { color: '#4ce1b6', backgroundColor: 'rgba(76, 225, 182, 0.08)' },
        }}
      >
        {visible ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
      </IconButton>
    </InputAdornment>
  );

  if (isCompact) {
    return (
      <MobileAuthShell
        footer={
          <Typography variant="caption" sx={authCopyrightSx}>
            {AUTH_COPYRIGHT}
          </Typography>
        }
      >
        <AuthMobileCard
          title="Create new password"
          subtitle="Choose a strong password with at least 8 characters."
        >
          {successMessage && (
            <Alert severity="success" sx={darkSuccessAlertSx}>
              {successMessage}
            </Alert>
          )}
          {errors.non_field_errors && (
            <Alert severity="error" sx={darkAlertSx}>
              {Array.isArray(errors.non_field_errors)
                ? errors.non_field_errors.map((error, index) => <div key={index}>{error}</div>)
                : errors.non_field_errors}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
            <AuthDarkTextField
              type={showPassword ? 'text' : 'password'}
              label="New Password"
              name="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setErrors({});
              }}
              autoFocus
              error={!!errors.password}
              helperText={
                errors.password
                  ? Array.isArray(errors.password)
                    ? errors.password.join(', ')
                    : errors.password
                  : ''
              }
              InputProps={{
                endAdornment: passwordToggle(showPassword, () => setShowPassword(!showPassword)),
              }}
            />
            <AuthDarkTextField
              type={showConfirmPassword ? 'text' : 'password'}
              label="Confirm New Password"
              name="confirm_password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setErrors({});
              }}
              error={!!errors.confirm_password}
              helperText={
                errors.confirm_password
                  ? Array.isArray(errors.confirm_password)
                    ? errors.confirm_password.join(', ')
                    : errors.confirm_password
                  : ''
              }
              InputProps={{
                endAdornment: passwordToggle(showConfirmPassword, () =>
                  setShowConfirmPassword(!showConfirmPassword)
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isSubmitting || !!successMessage}
              sx={darkPrimaryButtonSx}
            >
              {isSubmitting ? 'Resetting...' : successMessage ? 'Redirecting...' : 'Reset password'}
            </Button>
          </Box>
        </AuthMobileCard>
      </MobileAuthShell>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        width: '100%',
        backgroundColor: '#ffffff',
      }}
    >
      <AuthBrandingPanel />

      {/* Right Panel - Reset Password Form */}
      <Box
        className={`login-form-panel ${formPanelThemeClass}`}
        sx={{
          ...authFormPanelSx,
          bgcolor: '#ffffff',
          padding: { xs: 3, sm: 4, md: 6, lg: 8 },
        }}
      >
        <Container
          maxWidth="sm"
          sx={{
            width: '100%',
            px: { xs: 0, sm: 2 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            flex: 1,
            '@media (min-width: 600px)': {
              maxWidth: '400px',
            },
          }}
        >
          {/* Logo */}
          <Box
            component="img"
            src={getStaticFileUrl('topbar/logo_ed.png')}
            alt="RoomNexa"
            sx={{
              height: '40px',
              width: 'auto',
              mb: 4,
              mx: 'auto',
              display: 'block',
            }}
          />

          {/* Title */}
          <Typography
            component="span"
            className="auth-form-title"
            sx={{
              display: 'block',
              fontWeight: 500,
              mb: 3,
              color: '#111827',
              fontSize: { xs: '24px', sm: '28px', md: '32px' },
              textAlign: 'left',
            }}
          >
            Create new password
          </Typography>

          {/* Success Message */}
          {successMessage && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {successMessage}
            </Alert>
          )}

          {/* Error Messages */}
          {errors.non_field_errors && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {Array.isArray(errors.non_field_errors)
                ? errors.non_field_errors.map((error, index) => <div key={index}>{error}</div>)
                : errors.non_field_errors}
            </Alert>
          )}

          {/* Reset Password Form */}
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
            {/* Password Input */}
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label="New Password"
              name="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setErrors({});
              }}
              required={false}
              autoFocus
              error={!!errors.password}
              helperText={errors.password ? (Array.isArray(errors.password) ? errors.password.join(', ') : errors.password) : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{
                        color: '#757575',
                        padding: '8px',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      {showPassword ? <VisibilityOff sx={{ fontSize: '20px' }} /> : <Visibility sx={{ fontSize: '20px' }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                  backgroundColor: '#ffffff',
                  fontSize: '16px',
                  height: '56px',
                  color: '#212121',
                  '&:hover fieldset': {
                    borderColor: '#4ce1b6',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#4ce1b6',
                    borderWidth: '1px',
                  },
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                    borderWidth: '1px',
                  },
                },
                '& .MuiInputLabel-root': {
                  fontSize: '16px',
                  color: '#757575',
                  '&.Mui-focused': {
                    color: '#4ce1b6',
                  },
                },
                '& .MuiInputBase-input': {
                  fontSize: '16px',
                  color: '#212121',
                  padding: '16.5px 14px',
                },
                '& .MuiFormHelperText-root': {
                  fontSize: '12px',
                  marginLeft: 0,
                  marginTop: '4px',
                },
              }}
            />

            {/* Confirm Password Input */}
            <TextField
              fullWidth
              type={showConfirmPassword ? 'text' : 'password'}
              label="Confirm New Password"
              name="confirm_password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setErrors({});
              }}
              required={false}
              error={!!errors.confirm_password}
              helperText={errors.confirm_password ? (Array.isArray(errors.confirm_password) ? errors.confirm_password.join(', ') : errors.confirm_password) : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      sx={{
                        color: '#757575',
                        padding: '8px',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      {showConfirmPassword ? <VisibilityOff sx={{ fontSize: '20px' }} /> : <Visibility sx={{ fontSize: '20px' }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                  backgroundColor: '#ffffff',
                  fontSize: '16px',
                  height: '56px',
                  color: '#212121',
                  '&:hover fieldset': {
                    borderColor: '#4ce1b6',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#4ce1b6',
                    borderWidth: '1px',
                  },
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                    borderWidth: '1px',
                  },
                },
                '& .MuiInputLabel-root': {
                  fontSize: '16px',
                  color: '#757575',
                  '&.Mui-focused': {
                    color: '#4ce1b6',
                  },
                },
                '& .MuiInputBase-input': {
                  fontSize: '16px',
                  color: '#212121',
                  padding: '16.5px 14px',
                },
                '& .MuiFormHelperText-root': {
                  fontSize: '12px',
                  marginLeft: 0,
                  marginTop: '4px',
                },
              }}
            />

            {/* Submit Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isSubmitting || !!successMessage}
              sx={{
                py: 1.5,
                mb: 4,
                backgroundColor: '#000000',
                color: '#ffffff',
                borderRadius: '4px',
                textTransform: 'uppercase',
                fontSize: '14px',
                fontWeight: 500,
                height: '48px',
                '&:hover': {
                  backgroundColor: '#333333',
                },
                '&:disabled': {
                  backgroundColor: '#bdbdbd',
                  color: '#ffffff',
                },
              }}
            >
              {isSubmitting ? 'RESETTING PASSWORD...' : successMessage ? 'REDIRECTING...' : 'RESET PASSWORD'}
            </Button>
          </Box>

          {/* Copyright - Centered at bottom */}
          <Box
            sx={{
              mt: 4,
              pt: 4,
              textAlign: 'center',
              width: '100%',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: '#9e9e9e',
                fontSize: '12px',
              }}
            >
              © 2025 RoomNexa, Inc. Singapore · Worldwide
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default ResetPasswordPage;

